import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { HeroLayout3 } from '@components/hero/components/hero-layout-3/model';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { IconsDirective } from '@shared/directives';
import { ContextService } from '@services/context';
import { ResponsiveService } from '@services/responsive';

const AUTOPLAY_INTERVAL_MS = 5000;

@Component({
  selector: 'app-hero-layout-3',
  imports: [TitleComponent, IconsDirective, RouterLink, ImageComponent],
  templateUrl: './hero-layout-3.component.html',
  styleUrl: './hero-layout-3.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroLayout3Component implements OnInit {
  readonly slidesElementRef = viewChild.required<ElementRef<HTMLElement>>('slidesElementRef');
  readonly data = input.required<HeroLayout3>();
  title = signal<Title>(null!);

  readonly #contextService = inject(ContextService);
  readonly #responsiveService = inject(ResponsiveService);
  readonly #destroyRef = inject(DestroyRef);

  readonly slides = computed(() =>
    this.#responsiveService.breakpoints() === 'mobile' ? this.data().slidesMobile : this.data().slidesDesktop
  );

  currentScroll = signal(0);
  #autoplayTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      this.#responsiveService.breakpoints();
      this.currentScroll.set(0);
      if (this.#contextService.isBrowser()) {
        this.slidesElementRef().nativeElement.scrollTo({ left: 0 });
      }
    });

    afterNextRender(() => {
      this.#startAutoplay();
    });

    this.#destroyRef.onDestroy(() => this.#stopAutoplay());
  }

  ngOnInit(): void {
    this.title.set({
      level: 'H1',
      text: this.data().title,
      color: 'WHITE',
      align: {
        mobile: 'LEFT',
        desktop: 'LEFT',
      },
      size: {
        mobile: 'H1',
        desktop: 'H3',
      },
    });
  }

  onNext(length: number) {
    if (this.currentScroll() < this.slidesElementRef().nativeElement.clientWidth * (length - 1)) {
      this.currentScroll.set(this.currentScroll() + this.slidesElementRef().nativeElement.clientWidth);
    } else {
      this.currentScroll.set(0);
    }

    this.slidesElementRef().nativeElement.scrollTo({ left: this.currentScroll() });
    this.#resetAutoplay();
  }

  onPrev(length: number) {
    if (this.currentScroll() > 0) {
      this.currentScroll.set(this.currentScroll() - this.slidesElementRef().nativeElement.clientWidth);
    } else {
      this.currentScroll.set(this.slidesElementRef().nativeElement.clientWidth * (length - 1));
    }
    this.slidesElementRef().nativeElement.scrollTo({ left: this.currentScroll() });
    this.#resetAutoplay();
  }

  #startAutoplay(): void {
    if (!this.data().autoplay) return;
    this.#autoplayTimer = setInterval(() => {
      this.onNext(this.slides().length);
    }, AUTOPLAY_INTERVAL_MS);
  }

  #stopAutoplay(): void {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  #resetAutoplay(): void {
    if (!this.data().autoplay) return;
    this.#stopAutoplay();
    this.#startAutoplay();
  }
}
